import { useEffect, useRef, useState } from 'react';
import { SelectMenu, type SelectMenuOption } from './SelectMenu';

type FormSelectMenuProps = {
  name: string;
  options: SelectMenuOption[];
  defaultValue?: string;
  ariaLabel: string;
  className?: string;
};

/** A custom dropdown that still participates in FormData-based forms. */
export function FormSelectMenu({ name, options, defaultValue, ariaLabel, className = '' }: FormSelectMenuProps) {
  const initialValue = defaultValue ?? options[0]?.value ?? '';
  const [value, setValue] = useState(initialValue);
  const rootRef = useRef<HTMLDivElement>(null);
  const previousDefault = useRef(defaultValue);

  useEffect(() => {
    if (previousDefault.current !== defaultValue) {
      previousDefault.current = defaultValue;
      setValue(defaultValue ?? options[0]?.value ?? '');
    }
  }, [defaultValue, options]);

  useEffect(() => {
    if (!options.some(option => option.value === value)) setValue(defaultValue ?? options[0]?.value ?? '');
  }, [defaultValue, options, value]);

  useEffect(() => {
    const form = rootRef.current?.closest('form');
    if (!form) return;
    const reset = () => setValue(defaultValue ?? options[0]?.value ?? '');
    form.addEventListener('reset', reset);
    return () => form.removeEventListener('reset', reset);
  }, [defaultValue, options]);

  return <div className="form-select-menu" ref={rootRef}>
    <input type="hidden" name={name} value={value} />
    <SelectMenu className={className} value={value} onChange={setValue} options={options} ariaLabel={ariaLabel} />
  </div>;
}
